import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, UserCheck, UserX, Search, Download, CheckSquare, Square, Filter, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { getAllUsers, updateUserRole, updateUser } from '../lib/firestore';

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users when search or filters change
  useEffect(() => {
    let result = users;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(user => 
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query)
      );
    }
    
    // Role filter
    if (roleFilter !== 'all') {
      result = result.filter(user => user.role === roleFilter);
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(user => 
        statusFilter === 'active' 
          ? user.status !== 'disabled' 
          : user.status === 'disabled'
      );
    }
    
    setFilteredUsers(result);
    setSelectedUsers([]);
    setSelectAll(false);
  }, [users, searchQuery, roleFilter, statusFilter]);

  const fetchUsers = async () => {
    try {
      const usersData = await getAllUsers();
      setUsers(usersData);
      setFilteredUsers(usersData);
    } catch (error) {
      toast.error('Failed to load users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success('User role updated');
    } catch (error) {
      toast.error('Failed to update role');
      console.error(error);
    }
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'disabled' : 'active';
    try {
      await updateUser(userId, { status: newStatus });
      setUsers(users.map(u => u.id === userId ? { ...u, status: newStatus } : u));
      toast.success(`User ${newStatus === 'active' ? 'reactivated' : 'disabled'} successfully`);
    } catch (error) {
      toast.error('Failed to update user status');
      console.error(error);
    }
  };

  // Bulk actions
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectUser = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const handleBulkDisable = async () => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }
    
    try {
      for (const userId of selectedUsers) {
        await updateUser(userId, { status: 'disabled' });
      }
      setUsers(users.map(u => 
        selectedUsers.includes(u.id) ? { ...u, status: 'disabled' } : u
      ));
      setSelectedUsers([]);
      setSelectAll(false);
      toast.success(`${selectedUsers.length} user(s) disabled`);
    } catch (error) {
      toast.error('Failed to disable users');
      console.error(error);
    }
  };

  const handleBulkReactivate = async () => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }
    
    try {
      for (const userId of selectedUsers) {
        await updateUser(userId, { status: 'active' });
      }
      setUsers(users.map(u => 
        selectedUsers.includes(u.id) ? { ...u, status: 'active' } : u
      ));
      setSelectedUsers([]);
      setSelectAll(false);
      toast.success(`${selectedUsers.length} user(s) reactivated`);
    } catch (error) {
      toast.error('Failed to reactivate users');
      console.error(error);
    }
  };

  // Export functionality
  const handleExportCSV = () => {
    const headers = ['Name', 'Email', 'Role', 'Status', 'Email Verified', 'Created At'];
    const csvData = filteredUsers.map(user => [
      user.name || '',
      user.email || '',
      user.role || 'student',
      user.status || 'active',
      user.emailVerified ? 'Yes' : 'No',
      user.createdAt?.toDate ? user.createdAt.toDate().toISOString() : ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('User data exported successfully');
  };

  const handleExportJSON = () => {
    const exportData = filteredUsers.map(user => ({
      name: user.name,
      email: user.email,
      role: user.role,
      status: user.status || 'active',
      emailVerified: user.emailVerified || false,
      createdAt: user.createdAt?.toDate ? user.createdAt.toDate().toISOString() : null
    }));
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('User data exported successfully');
  };

  const clearFilters = () => {
    setSearchQuery('');
    setRoleFilter('all');
    setStatusFilter('all');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
        <div className="text-[#00E5FF] text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-users-page" className="min-h-screen bg-[#0B0E14] pb-12">
      <div className="bg-[#151923] border-b border-[#1E293B]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Button
            data-testid="back-btn"
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4 text-[#00E5FF] hover:text-[#33EBFF] hover:bg-[#1E293B]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-[#00E5FF]" />
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                User Management
              </h1>
            </div>
            
            {/* Export Buttons */}
            <div className="flex gap-2">
              <Button
                data-testid="export-csv-btn"
                variant="outline"
                onClick={handleExportCSV}
                className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button
                data-testid="export-json-btn"
                variant="outline"
                onClick={handleExportJSON}
                className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]"
              >
                <Download className="w-4 h-4 mr-2" />
                Export JSON
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
            <div className="text-sm text-[#94A3B8] mb-1">Total Users</div>
            <div className="text-3xl font-bold text-[#F8FAFC]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {users.length}
            </div>
          </div>
          <div className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
            <div className="text-sm text-[#94A3B8] mb-1">Active Users</div>
            <div className="text-3xl font-bold text-[#10B981]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {users.filter(u => u.status !== 'disabled').length}
            </div>
          </div>
          <div className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
            <div className="text-sm text-[#94A3B8] mb-1">Disabled Users</div>
            <div className="text-3xl font-bold text-[#EF4444]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {users.filter(u => u.status === 'disabled').length}
            </div>
          </div>
          <div className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
            <div className="text-sm text-[#94A3B8] mb-1">Verified Users</div>
            <div className="text-3xl font-bold text-[#6366F1]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {users.filter(u => u.emailVerified).length}
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-[#151923] border border-[#1E293B] rounded-md p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#0B0E14] border-[#1E293B] text-[#F8FAFC] placeholder-[#64748B]"
              />
            </div>
            
            {/* Role Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-[#64748B]" />
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger data-testid="role-filter" className="w-[150px] bg-[#0B0E14] border-[#1E293B] text-[#F8FAFC]">
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent className="bg-[#151923] border-[#1E293B]">
                  <SelectItem value="all" className="text-[#F8FAFC]">All Roles</SelectItem>
                  <SelectItem value="student" className="text-[#F8FAFC]">Student</SelectItem>
                  <SelectItem value="instructor" className="text-[#F8FAFC]">Instructor</SelectItem>
                  <SelectItem value="admin" className="text-[#F8FAFC]">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter" className="w-[150px] bg-[#0B0E14] border-[#1E293B] text-[#F8FAFC]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#151923] border-[#1E293B]">
                <SelectItem value="all" className="text-[#F8FAFC]">All Status</SelectItem>
                <SelectItem value="active" className="text-[#F8FAFC]">Active</SelectItem>
                <SelectItem value="disabled" className="text-[#F8FAFC]">Disabled</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Clear Filters */}
            {(searchQuery || roleFilter !== 'all' || statusFilter !== 'all') && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="text-[#94A3B8] hover:text-[#F8FAFC]"
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
          
          {/* Results count */}
          <div className="mt-3 text-sm text-[#64748B]">
            Showing {filteredUsers.length} of {users.length} users
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <div className="bg-[#1E293B] border border-[#334155] rounded-md p-4 mb-6 flex items-center justify-between">
            <div className="text-[#F8FAFC]">
              <span className="font-semibold">{selectedUsers.length}</span> user(s) selected
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="bulk-disable-btn"
                variant="outline"
                onClick={handleBulkDisable}
                className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"
              >
                <UserX className="w-4 h-4 mr-2" />
                Disable Selected
              </Button>
              <Button
                data-testid="bulk-reactivate-btn"
                variant="outline"
                onClick={handleBulkReactivate}
                className="border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Reactivate Selected
              </Button>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-[#151923] border border-[#1E293B] rounded-md overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1E293B]">
                <th className="text-left px-4 py-4">
                  <button
                    onClick={handleSelectAll}
                    className="flex items-center text-[#94A3B8] hover:text-[#F8FAFC]"
                    data-testid="select-all-btn"
                  >
                    {selectAll ? (
                      <CheckSquare className="w-5 h-5 text-[#00E5FF]" />
                    ) : (
                      <Square className="w-5 h-5" />
                    )}
                  </button>
                </th>
                <th className="text-left px-4 py-4 text-sm font-semibold text-[#94A3B8]">Name</th>
                <th className="text-left px-4 py-4 text-sm font-semibold text-[#94A3B8]">Email</th>
                <th className="text-left px-4 py-4 text-sm font-semibold text-[#94A3B8]">Role</th>
                <th className="text-left px-4 py-4 text-sm font-semibold text-[#94A3B8]">Status</th>
                <th className="text-left px-4 py-4 text-sm font-semibold text-[#94A3B8]">Verified</th>
                <th className="text-left px-4 py-4 text-sm font-semibold text-[#94A3B8]">Change Role</th>
                <th className="text-left px-4 py-4 text-sm font-semibold text-[#94A3B8]">Account</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-[#64748B]">
                    No users found matching your criteria
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const isDisabled = user.status === 'disabled';
                  const isSelected = selectedUsers.includes(user.id);
                  return (
                    <tr key={user.id} data-testid={`user-row-${user.id}`} className={`border-b border-[#1E293B] last:border-0 ${isDisabled ? 'opacity-60' : ''} ${isSelected ? 'bg-[#1E293B]/50' : ''}`}>
                      <td className="px-4 py-4">
                        <button
                          onClick={() => handleSelectUser(user.id)}
                          className="flex items-center text-[#94A3B8] hover:text-[#F8FAFC]"
                          data-testid={`select-user-${user.id}`}
                        >
                          {isSelected ? (
                            <CheckSquare className="w-5 h-5 text-[#00E5FF]" />
                          ) : (
                            <Square className="w-5 h-5" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${isDisabled ? 'bg-[#1E293B] text-[#64748B]' : 'bg-[#1E293B] text-[#00E5FF]'}`}>
                            {user.name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <span className={`font-medium ${isDisabled ? 'text-[#64748B]' : 'text-[#F8FAFC]'}`}>{user.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-[#94A3B8]">{user.email}</td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          user.role === 'admin' ? 'bg-[#EF4444]/20 text-[#EF4444]' :
                          user.role === 'instructor' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                          'bg-[#10B981]/20 text-[#10B981]'
                        }`}>
                          {user.role?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          isDisabled 
                            ? 'bg-[#EF4444]/20 text-[#EF4444]' 
                            : 'bg-[#10B981]/20 text-[#10B981]'
                        }`}>
                          {isDisabled ? 'DISABLED' : 'ACTIVE'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          user.emailVerified 
                            ? 'bg-[#6366F1]/20 text-[#6366F1]' 
                            : 'bg-[#F59E0B]/20 text-[#F59E0B]'
                        }`}>
                          {user.emailVerified ? 'VERIFIED' : 'PENDING'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <Select
                          value={user.role}
                          onValueChange={(value) => handleRoleChange(user.id, value)}
                          disabled={isDisabled}
                        >
                          <SelectTrigger data-testid={`role-select-${user.id}`} className="w-[130px] bg-[#0B0E14] border-[#1E293B] text-[#F8FAFC]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#151923] border-[#1E293B]">
                            <SelectItem value="student" className="text-[#F8FAFC]">Student</SelectItem>
                            <SelectItem value="instructor" className="text-[#F8FAFC]">Instructor</SelectItem>
                            <SelectItem value="admin" className="text-[#F8FAFC]">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-4">
                        <Button
                          data-testid={`status-toggle-${user.id}`}
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusToggle(user.id, user.status || 'active')}
                          className={`flex items-center gap-2 ${
                            isDisabled 
                              ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' 
                              : 'border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10'
                          }`}
                        >
                          {isDisabled ? (
                            <>
                              <UserCheck className="w-4 h-4" />
                              Reactivate
                            </>
                          ) : (
                            <>
                              <UserX className="w-4 h-4" />
                              Disable
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminUsersPage;
